<!--
  The non-closable "החשבון הוקפא" cover. App.vue mounts it over the logged-in shell when the root
  admin froze the account (appData.frozen, or any request refused with frozen: true - see the
  axiosV2 interceptor), so the freeze is seen INSIDE the app the user knows and not as a login
  error. There is no close button, no backdrop click and no Escape: the ways out are the support
  line (WhatsApp / phone), logging out, or the freeze being lifted - account-status is polled once
  a minute and the page reloads itself the moment the account is active again.
-->
<template>
  <div class="accountFrozenModal" role="dialog" aria-modal="true" aria-labelledby="accountFrozenTitle">
    <div class="accountFrozenCard">

      <div class="accountFrozenIcon">
        <i class="fa-solid fa-snowflake"></i>
      </div>

      <h2 id="accountFrozenTitle" class="accountFrozenTitle">החשבון הוקפא</h2>

      <p class="accountFrozenText">לפרטים פנו לתמיכה</p>

      <p class="accountFrozenSub">
        החשבון שלכם בפלאנאפ הוקפא ולא ניתן להשתמש במערכת כרגע.
        <br>
        נשמח לעזור ולהחזיר אתכם לעבודה.
      </p>

      <div class="accountFrozenActions">
        <a class="accountFrozenBtn accountFrozenWhatsapp"
           :href="whatsappLink"
           target="_blank"
           rel="noopener">
          <i class="fa-brands fa-whatsapp"></i>
          וואטסאפ לתמיכה
        </a>
        <a class="accountFrozenBtn accountFrozenPhone"
           :href="'tel:' + supportPhone">
          <i class="fa-solid fa-phone"></i>
          <span dir="ltr">{{ supportPhoneDisplay }}</span>
        </a>
      </div>

      <div class="accountFrozenFooter">
        <span class="accountFrozenHint">
          <i class="fa-regular fa-clock"></i>
          ברגע שההקפאה תוסר, העמוד ייפתח מחדש מעצמו
        </span>
        <button type="button"
                class="accountFrozenLogout"
                @click="logout">
          <i class="fa-solid fa-arrow-right-from-bracket"></i>
          התנתקות
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import axiosV2 from '@/core/http/axios_api_v2';

//+972 54-335-7624 - PlanUp's support line (SUPPORT_PHONE in apiv2/modules/support)
const SUPPORT_PHONE = '0543357624';

export default {
  name: 'AccountFrozenModal',

  data() {
    return {
      supportPhone: SUPPORT_PHONE,
      timer: null,
      checking: false
    };
  },

  computed: {

    supportPhoneDisplay() {
      return this.supportPhone.replace(/^(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3');
    },

    whatsappLink() {
      return 'https://wa.me/972' + this.supportPhone.replace(/^0/, '')
        + '?text=' + encodeURIComponent('היי, החשבון שלי בפלאנאפ הוקפא ואשמח לפרטים');
    }

  },

  mounted() {
    //a lifted freeze lets the page back in without the user doing anything
    this.timer = setInterval(this.checkStatus, 60000);
  },

  beforeDestroy() {
    clearInterval(this.timer);
  },

  methods: {

    //account-status is one of the few calls the api still answers for a frozen account
    checkStatus() {

      if (this.checking)
        return Promise.resolve(false);

      this.checking = true;

      return axiosV2.get('account-status', { params: { t: Date.now() } })
        .then(res => {
          this.checking = false;
          if (res && res.data && res.data.frozen === false) {
            window.location.reload();
            return true;
          }
          return false;
        })
        .catch(() => {
          this.checking = false;
          return false;
        });

    },

    logout() {
      this.$store.dispatch('logoff');
    }

  }
};
</script>

<style scoped>
.accountFrozenModal {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  direction: rtl;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  overscroll-behavior: contain;
  animation: accountFrozenFade 0.25s ease-out;
}

.accountFrozenCard {
  width: 100%;
  max-width: 470px;
  background: #fff;
  border-radius: 22px;
  padding: 40px 36px 22px;
  text-align: center;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.35);
  animation: accountFrozenPop 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.15);
}

.accountFrozenIcon {
  width: 88px;
  height: 88px;
  margin: 0 auto 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e9f1ff, #d6e5ff);
  box-shadow: inset 0 0 0 7px #f3f7ff;
  color: #3b6fd8;
  font-size: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.accountFrozenTitle {
  font-size: 27px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 6px;
}

.accountFrozenText {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px;
}

.accountFrozenSub {
  font-size: 14px;
  line-height: 1.7;
  color: #6b7280;
  margin: 0 0 26px;
}

.accountFrozenActions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.accountFrozenBtn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.accountFrozenBtn:hover {
  transform: translateY(-1px);
}

.accountFrozenWhatsapp {
  background: #25d366;
  color: #fff !important;
  box-shadow: 0 8px 20px rgba(37, 211, 102, 0.3);
}

.accountFrozenWhatsapp:hover {
  box-shadow: 0 10px 24px rgba(37, 211, 102, 0.4);
}

.accountFrozenPhone {
  background: #f3f0fb;
  color: #6d4fc2 !important;
}

.accountFrozenPhone:hover {
  background: #ebe5f8;
}

.accountFrozenFooter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  border-top: 1px solid #eef0f4;
  padding-top: 14px;
}

.accountFrozenHint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #9aa3b2;
}

.accountFrozenLogout {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
}

.accountFrozenLogout:hover {
  background: #f3f4f6;
  color: #374151;
}

@keyframes accountFrozenFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes accountFrozenPop {
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to { opacity: 1; transform: none; }
}

@media (max-width: 480px) {
  .accountFrozenCard {
    padding: 32px 22px 18px;
  }
  .accountFrozenFooter {
    justify-content: center;
  }
}
</style>
